import Comment, { OptimisticComment } from "./Comment";
import { CommentEditor } from ".";
import { useTopicsStoreProvider, useTopicsDataStore, ThreadContextProvider, useThreadContext, useScopedTopicsInstance, useTopicsPanelProvier } from "./../Context";
import { getFormattedDate, } from "./../utils";
import { useThreadActions, useOptimisticComment } from "./../hooks";
import Dropdown from "@/react/components/Dropdown";
import Swal from '@/react/components/Swal'
import { Fragment, useEffect, useRef } from "react";

export default function Thread({ id, inFloatingPanel = false }) {
	const topicsInstance = useScopedTopicsInstance();
	const { currentUser } = topicsInstance;
	const { activeTab } = useTopicsPanelProvier()
	const ref = useRef(null);

	const thread = useTopicsDataStore(state => state.threads.get(id));

	const comments = useTopicsStoreProvider(s => s.mapThreadToComments.get(id)) || new Set();
	const commentIds = Array.from(comments);

	const { isPinned, isResolved, title } = thread

	const { optimisticList: optimisticComments, create: createComment, retry: retryComment } =
		useOptimisticComment({
			threadId: id,
			onSuccessStoreFn: (real) => topicsInstance.addThreadComment(id, real)
		});

	const { pin, unpin, resolve, unresolve, remove, copy, isLoading } = useThreadActions(thread);

	const handleResolved = async () => {
		const result = await Swal.fire({
			title: `Are you sure?`,
			text: `Are you sure you want to ${isResolved ? 'unresolve' : 'resolve'} this thread?`,
			icon: 'warning',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: `${isResolved ? 'Unresolve' : 'Resolve'}`
		})

		result.isConfirmed && (isResolved ? unresolve() : resolve());
	}

	const handlePinned = () => {
		isPinned ? unpin() : pin();
	}

	const handleCopy = () => {
		copy();
	}

	const handleDelete = async () => {
		const result = await Swal.fire({
			title: `Are you sure?`,
			html: "Are you sure you want to delete this thread?<br>This action cannot be undone.",
			icon: 'warning',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Delete thread'
		})

		result.isConfirmed && remove();
	}

	useEffect(() => {
		if (inFloatingPanel && ref.current) {
			// scroll to the bottom of the thread
			ref.current.scrollTop = ref.current.scrollHeight;
		}
	}, [inFloatingPanel, ref.current]);

	if (!thread || (
		activeTab !== 'all' &&
		(
			(activeTab === 'resolved' && !thread.isResolved) ||
			(activeTab === 'unresolved' && thread.isResolved) ||
			(activeTab === 'pinned' && !thread.isPinned)
		)
	))
		return null;

	const isThreadOwner = thread.userId === currentUser.id;
	const isNamespaceAdmin = topicsInstance.isNamespaceAdmin;
	const canManage = topicsInstance.canManageThreads;
	return (
		<ThreadContextProvider thread={{ ...thread, handleResolved, handlePinned, handleCopy, handleDelete, canManage, isThreadOwner, isNamespaceAdmin }}>
			<div data-thread-id={id} className={`flex flex-col gap-2 text-black text-article p-3 rounded-primary ${inFloatingPanel ? 'pr-2' : ''} ${isLoading ? 'thread-comment-loading' : ''}`}>
				<ThreadStatus />

				<ThreadHeader />

				<div ref={ref} className={`flex flex-col gap-4 ${inFloatingPanel && 'max-h-[350px] overflow-y-auto pr-1'}`}>
					{commentIds.map((id, index) => (
						<Comment
							key={id}
							id={id}
							previousCommentId={commentIds[index - 1]} // just a hack for date separator
						/>
					))}

					{/* Render optimistic comments */}
					{optimisticComments.map((optimisticComment) => (
						<OptimisticComment
							key={optimisticComment.id}
							optimisticComment={optimisticComment}
							onRetry={retryComment}
						/>
					))}
				</div>

				{
					!isResolved && <div className={`pt-2 ${inFloatingPanel && 'pr-2'}`}>
						<CommentEditor
							user={currentUser}
							editorProps={{
								placeholder: `Reply to ${title}`,
								onSubmit: createComment,
								focus: inFloatingPanel,
							}}
						/>
					</div>
				}
			</div>
		</ThreadContextProvider>
	);
}


const ThreadStatus = () => {
	const { createdAt, isResolved } = useThreadContext();

	const time = getFormattedDate(createdAt);

	return (
		<div className={`w-full flex items-center text-grey-text text-xs font-medium ${isResolved && '[&_hr]:border-primary'} bg-white`}>
			<hr className="flex-1" />
			<span className="rounded-full px-4 py-1 bg-white border-normal border-grey">{time}</span>
			<div className="flex-1 flex items-center gap-1 grow-[1]">
				<hr className="grow" />
				{isResolved ? <span className="text-primary">Resolved</span> : null}
			</div>
		</div>
	)
}

const ThreadHeader = () => {
	const { title, isPinned, isResolved, handleCopy, handleDelete, handlePinned, handleResolved, canManage, isThreadOwner, isNamespaceAdmin } = useThreadContext();

	const menuItems = [
		{
			key: 'resolve',
			element: <span className="hover:text-primary hover:cursor-pointer" onClick={handleResolved}>{isResolved ? "Unresolve" : " Mark as resolved"}</span>,
			visible: isNamespaceAdmin || isThreadOwner || canManage,
		},
		{
			key: 'pin',
			element: <span className="hover:text-primary hover:cursor-pointer" onClick={handlePinned}>{!isPinned ? 'Pin' : 'Unpin'} this thread</span>,
			visible: isNamespaceAdmin || isThreadOwner || canManage,
		},
		{
			key: 'copy',
			element: <span className="hover:text-primary hover:cursor-pointer" onClick={handleCopy}>Copy link to thread</span>,
			visible: true, // always visible
		},
		{
			key: 'delete',
			element: <span className="hover:text-primary hover:cursor-pointer" onClick={handleDelete}>Delete thread</span>,
			visible: isNamespaceAdmin || isThreadOwner,
		},
	];


	return <div className="flex items-center justify-end">
		{/* <span className="rounded-primary border-primary px-2 py-0.5 text-primary border-thin mr-auto">Resolved</span> */}
		{
			isPinned ? <span className="font-MaterialIcons text-lg text-primary align-middle -rotate-45 mr-1">push_pin</span> : null
		}
		<span className={`text-label font-semibold text-grey-text`}>{title}</span>

		<Dropdown>
			<Dropdown.Trigger>
				<button className="btn btn-icon text-xl font-normal bg-transparent hover:text-primary text-grey-text">more_vert</button>
			</Dropdown.Trigger>
			<Dropdown.List>
				<div className="hidden flex flex-col gap-2 text-placeholder" data-arrow="false">
					{menuItems.filter(item => item.visible).map(item =>
						<Fragment key={item.key}>{item.element}</Fragment>
					)}
				</div>
			</Dropdown.List>
		</Dropdown>
	</div>
}